import CustomForm from "../CustomForm";
import m, {Vnode, VnodeDOM} from "mithril";
import ICustomFormSettings from "../ICustomFormSettings";
import {LabelItem} from "../../model/items/labelItem";
import {RadioItem} from "../../model/items/radio";
import BatteryCard from "./BatteryCard";
import Design from "../../ui/design";
import {ImageItem} from "../../model/items/imageItem";
import {loadJsonFromString} from "../shared/helpers";
import Image from "../shared/components/Image/Image";
import {CheckboxItem} from "../../model/items/checkbox";


export type Answer = {radio: RadioItem, label: string};

/**
 * Battery
 *
 * Transform battery into cards
 */
export default abstract class Battery extends CustomForm {

    // Pointer to currently working question
    protected currentQuestionIndex: number = 0;

    // Pointer to currently visible options
    protected currentOptionsIndex: number = 0;

    protected readonly questionCards: BatteryCard[] = [];

    // Enable auto submit of battery
    protected autoSubmit: boolean = false;
    protected autoSubmitNextForm: boolean = false;
    private forceMobileView: boolean = false;

    // Battery form
    private $form?: JQuery;

    private lastRadioCheckboxRow = 0;
    private lastRadioCheckboxColumn = 0;

    constructor(settings: ICustomFormSettings) {
        super(settings);

        loadJsonFromString(settings.form.aux1?.toLowerCase(), settings.form.questionary, (data) => {
            this.autoSubmit = Boolean(data?.['autosubmit']);
            this.autoSubmitNextForm = Boolean(data?.['autosubmitnextform']);
            this.forceMobileView = Boolean(data?.['forcemobileview']);
        });

        const grid = settings.form.grid;

        let batteryFirstRow = 0;

        // Image on first row
        this.heroImage = undefined;
        for (let col=0; col<grid.columns; col++) {
            for (const cellItem of grid.layout[0][col].cellItems) {
                if (cellItem instanceof ImageItem) {
                    this.heroImage = new Image(cellItem);
                    batteryFirstRow = 1;
                    break;
                }
            }
            if (this.heroImage) {
                break;
            }
        }

        // Answers
        const labels = [];
        for (let col=1; col<grid.columns; col++) {
            for (const cellItem of grid.layout[batteryFirstRow][col].cellItems) {
                if (cellItem instanceof LabelItem) {
                    labels[col] = cellItem.childrenTags[0]?.value || '';
                }
            }
        }

        const randomization = settings.form.questionary.state.random.form[settings.form.id];

        // Find bottom row of battery
        for (let row=batteryFirstRow+1; row<grid.rows; row++) {
            const cellItem = grid.layout[row][2]?.cellItems[0] ?? grid.layout[row][1]?.cellItems[0];
            if (row > this.lastRadioCheckboxRow && (cellItem instanceof CheckboxItem || cellItem instanceof RadioItem)) {
                this.lastRadioCheckboxRow = row;
            }
        }

        // Find most right column of battery ... a tím fakt nemyslím nejsprávnější
        for (let column=1; column<grid.columns; column++) {
            const row = batteryFirstRow+1;
            const cellItem = grid.layout[row][column]?.cellItems[0];
            if (column > this.lastRadioCheckboxColumn && (cellItem instanceof CheckboxItem || cellItem instanceof RadioItem)) {
                this.lastRadioCheckboxColumn = column;
            }
        }

        // Question cards
        for (let row=batteryFirstRow+1; row<=this.lastRadioCheckboxRow; row++) {

            const rrow = randomization ? randomization.rows[row] : row;

            const answers: Answer[] = [];
            let question = '';
            let isEnabled = false;
            let imageUrl = '';

            for (let col = 0; col < grid.columns; col++) {
                const cell = grid.layout[rrow][col];
                isEnabled = isEnabled || (cell.isVisible() && cell.enabled);

                for (const cellItem of cell.cellItems) {
                    if (cellItem instanceof RadioItem) {
                        answers.push({label: labels[col], radio: cellItem});
                    } else if (cellItem instanceof LabelItem) {
                        question = cellItem.childrenTags[0]?.value || '';
                    } else if (cellItem instanceof ImageItem) {
                        imageUrl = this.settings.form.questionary.getFileUrl(cellItem.file);
                    }
                }
            }

            if (isEnabled) {
                const card = new BatteryCard(
                    question,
                    answers,
                    settings.form.questionary
                );
                card.imageUrl = imageUrl;

                this.questionCards[rrow] = card;
            }
        }

        this.questionCards = this.questionCards.filter(q => q); // reindex questions from 0
        for (const i in this.questionCards) {
            this.questionCards[i].progress = {current: Number(i)+1, total: this.questionCards.length};
        }

        // Pointers on current answer and options
        for(let i=0; i < this.questionCards.length; i++) {
            this.currentQuestionIndex = i;
            this.currentOptionsIndex = i;
            if (this.questionCards[i].answers[0]?.radio.empty()) {
                break;
            }
        }
    }

    protected get questionCard(): BatteryCard {
        return this.questionCards[this.currentQuestionIndex];
    };


    public rewriteFormControls() {
        return true;
    }

    public checkRequirements(): boolean {

        if (!this.forceMobileView && window.innerWidth >= Design.BC.md && window.innerHeight > 575) {
            console.log(`customForm-Battery: This form is disabled for large screen. You can prevent this behavior by set 'forceMobileView' to true in aux1.`);
            return false;
        }

        const firstBatteryRow = this.heroImage ? 1 : 0;

        for (const cell of this.settings.form.grid.cells) {
            if (cell.row < firstBatteryRow || cell.row > this.lastRadioCheckboxRow || cell.column > this.lastRadioCheckboxColumn) {
                continue;
            }
            if (cell.row === firstBatteryRow || cell.column === 0) {
                const labelItem = cell.cellItems.filter(ci => ci instanceof LabelItem)[0];
                if (!labelItem || !labelItem.childrenTags[0]?.value) {
                    console.log(`customForm-Battery: Item on row ${cell.row} and col ${cell.column} must be a Label with some value.`);
                    return false
                }
            } else if (!(cell.cellItems.filter(ci => ci instanceof RadioItem)[0])) {
                console.log(`customForm-Battery: Item on row ${cell.row} and col ${cell.column} must be a Radio`);
                return false;
            }
        }

        return true;
    }

    protected onAnswerSelect(answer?: Answer): void {
        answer?.radio.set();
        if (this.autoSubmit) {
            if (this.canNext()) {
                this.questionCard.makeAnimation('disappear-up-33-66', () => this.canNext() && this.nextQuestion());
            } else if (this.autoSubmitNextForm && this.currentQuestionIndex === this.questionCards.length-1) {
                this.questionCard.makeAnimation('disappear-up-33-66', () => this.settings.nextForm());
            }
        }
    }

    //-----------------/
    // VIEWS
    //
    public view() {
        const content = [
            m("form", {}, [
                this.viewHeroVideo(),
                this.viewHeroImage(),
                m( this.questionCard ),
                this.viewOptions(),
            ]),
        ];

        return this.viewLayout(content);

    };

    protected viewControls(nextButtonText: string = 'nextButton') {
        const form = this.settings.form;
        const questionary = form.questionary;

        const canPrevQuestion = this.canPrevious();
        const canNextQuestion = this.canNext();

        const visiblePreviousButton = this.settings.app.prevEnabled() || canPrevQuestion;
        const visibleLangButton = this.settings.app.langSwitcherEnabled();
        const visibleNextButton = this.settings.app.nextEnabled() || canNextQuestion;
        const enableNextButton = this.settings.form.validate() || canNextQuestion;

        return m(".controls", {},
            m(".control-group", {}, [
                visiblePreviousButton ? m("button.btn.large.outline.secondary#previousButton", {
                    onclick: () => {
                        if (canPrevQuestion) {
                            this.previousQuestion()
                        } else {
                            this.settings.prevForm()
                        }
                    },
                    title: questionary.renderText(questionary.messages.get('backButton')),
                }, [
                    m('i.icon.icon-chevron-left', {}, '')
                ]) : null,

                visibleLangButton ? m("button.btn.large.outline.secondary#languageButton", {
                    onclick: () => this.settings.selectLanguage(),
                    title: questionary.renderText(questionary.messages.get('language'))
                }, [
                    m('i.icon.icon-translate', {}, '')
                ]) : null,

                visibleNextButton ? m("button.btn.large.raised.primary.has-primary-background#nextButton", {
                    onclick: () => {
                        if (canNextQuestion) {
                            this.nextQuestion();
                        } else {
                            this.settings.nextForm();
                        }
                    },
                    disabled: enableNextButton ? false : 'disabled',
                }, [
                    m('span', {}, questionary.renderText(questionary.messages.get(nextButtonText)) ),
                    m('i.icon.icon-chevron-right'+(enableNextButton?'.text-light':''), {}, '')
                ]) : null,
            ])
        );
    }

    abstract viewOptions(): Vnode|null;


    //----------------/
    // CONTROL FORM
    //
    protected nextQuestion(): void {
        this.currentQuestionIndex = (this.currentQuestionIndex+1) % this.questionCards.length;
        this.currentOptionsIndex = this.currentQuestionIndex;

        if (this.$form) {
            this.$form?.find('.content').get(0)?.scrollIntoView();
        } else {
            this.settings.form.questionary.scrollTop();
        }
    }

    protected previousQuestion(): void {
        this.currentQuestionIndex = (this.currentQuestionIndex + this.questionCards.length-1) % this.questionCards.length;
        this.currentOptionsIndex = this.currentQuestionIndex;
    }

    protected canPrevious(): boolean {
        return this.currentQuestionIndex !== 0;
    }

    protected canNext(): boolean {
        return this.questionCard.isAnswered() && this.currentQuestionIndex < this.questionCards.length-1;
    }

    oncreate(vnode: VnodeDOM) {
        super.oncreate(vnode);

        this.$form = $(vnode.dom);
    }
}
